import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";

import { SetTenantLoadPaperworkBindingInput, SetTenantLoadPaperworkPolicyInput } from "~/gql/graphqlBase.generated";
import {
	SetTenantAvailabilityQuantityBasisMutation,
	SetTenantAvailabilityQuantityEntryMutation,
	SetTenantLoadPaperworkBindingMutation,
	SetTenantLoadPaperworkPolicyMutation,
	SetTenantOrderEntryUnitMutation,
	SetTenantPriceEntryUnitMutation,
	UpdateBusinessTimeZoneMutation,
	UpdateDocumentRenderModeMutation,
	UpdateFernieSuggestionsOnMutation,
	UpdateInvoicingSettingsMutation,
	UpdateOrderAcknowledgementReportMutation,
	UpdateOrderInvoiceReportMutation,
	UpdateOrderProFormaReportMutation,
	UpdateOrderPullSlipReportMutation,
	UpdatePricingCalcDecimalsMutation,
	UpdateProduceAutoLinkMatchingNumbersMutation,
	UpdateProduceDefaultReliefPercentMutation,
	UpdateProduceEnabledMutation,
	UpdateProducePlanFromOrderAccessMutation,
	UpdateProducePlanningEntryUnitMutation,
	UpdateProduceProductionEntryReviewMutation,
	UpdateProduceRoundingMutation,
	UpdateProduceSpaceConsumptionBasisMutation,
	UpdateProduceSpacingDimensionUnitMutation,
	UpdateProduceWeekDisplayAnchorMutation,
	UpdatePurchaseOrderReportMutation,
	UpdateTenantAutoAllocateExcludedGradesMutation,
	UpdateTenantBrandingMutation,
	UpdateTenantCompanyProfileMutation,
	UpdateTenantGradeLabelsMutation,
	UpdateTenantOrderEmailsEnabledMutation,
	UpdateTenantSignInPanelMutation,
	UpdateTrackShipmentWeightMutation
} from "~/gql/mutations/tenant.generated";
import { GetFernieSuggestionsSettingQuery, GetTenantQuery } from "~/gql/queries/tenant.generated";

const importApiEndpointsLazilyToBreakCycle = async () =>
	(await import("~/services/endpoints/apiEndpoints")).apiEndpoints;

type CycleFreeLifecycleDispatch = <ReturnValue, State>(
	thunkAction: ThunkAction<ReturnValue, State, unknown, UnknownAction>
) => ReturnValue;

interface SetTenantLoadPaperworkBindingLifecycle {
	dispatch: CycleFreeLifecycleDispatch;
	queryFulfilled: Promise<{ data: SetTenantLoadPaperworkBindingMutation }>;
}

interface SetTenantLoadPaperworkPolicyLifecycle {
	dispatch: CycleFreeLifecycleDispatch;
	queryFulfilled: Promise<{ data: SetTenantLoadPaperworkPolicyMutation }>;
}

export const applyLoadPaperworkPolicyPatch = (
	input: SetTenantLoadPaperworkPolicyInput,
	draft: GetTenantQuery
): void => {
	if (!draft.tenant) return;
	draft.tenant.loadPaperwork.policy = {
		__typename: "LoadPaperworkPolicyData",
		dropShip: input.dropShipKinds,
		regular: input.regularKinds
	};
};

export const applyLoadPaperworkBindingPatch = (
	input: SetTenantLoadPaperworkBindingInput,
	draft: GetTenantQuery
): void => {
	if (!draft.tenant) return;
	const bindings = draft.tenant.loadPaperwork.bindings;
	const index = bindings.findIndex((binding) => binding.kind === input.kind);
	if (!input.reportId) {
		if (index !== -1) bindings.splice(index, 1);
		return;
	}
	const entry = { __typename: "LoadPaperworkBindingData" as const, kind: input.kind, reportId: input.reportId };
	if (index === -1) bindings.push(entry);
	else bindings[index] = entry;
};

export const tenantEndpoints = {
	GetTenant: {
		// Default 60s RTK eviction would re-serialize the boot prefetch on later navigations.
		keepUnusedDataFor: 3600,
		providesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: GetTenantQuery) => res
	},
	GetFernieSuggestionsSetting: {
		keepUnusedDataFor: 3600,
		providesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: GetFernieSuggestionsSettingQuery) => res
	},
	UpdateDefaultFreightCharge: {
		invalidatesTags: ["Tenant"] as AddTagTypes
	},
	UpdateDefaultMinimumFreightCharge: { invalidatesTags: ["Tenant"] as AddTagTypes },
	UpdateDocumentRenderMode: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateDocumentRenderModeMutation) => res
	},
	UpdateStockByCustomerItemSettings: {
		invalidatesTags: ["Tenant"] as AddTagTypes
	},
	UpdateInvoicingSettings: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateInvoicingSettingsMutation) => res
	},
	UpdateStatementDeliverySettings: {
		invalidatesTags: ["Tenant"] as AddTagTypes
	},
	UpdateOrderAcknowledgementReport: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateOrderAcknowledgementReportMutation) => res
	},
	UpdateOrderInvoiceReport: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateOrderInvoiceReportMutation) => res
	},
	UpdateOrderProFormaReport: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateOrderProFormaReportMutation) => res
	},
	UpdatePurchaseOrderReport: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdatePurchaseOrderReportMutation) => res
	},
	UpdateOrderPullSlipReport: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateOrderPullSlipReportMutation) => res
	},
	UpdateProduceEnabled: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceEnabledMutation) => res
	},
	UpdateTrackShipmentWeight: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTrackShipmentWeightMutation) => res
	},
	UpdateFernieSuggestionsOn: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateFernieSuggestionsOnMutation) => res
	},
	UpdateTenantOrderEmailsEnabled: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTenantOrderEmailsEnabledMutation) => res
	},
	UpdateTenantBranding: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTenantBrandingMutation) => res
	},
	UpdateTenantCompanyProfile: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTenantCompanyProfileMutation) => res
	},
	UpdateTenantSignInPanel: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTenantSignInPanelMutation) => res
	},
	UpdateTenantGradeLabels: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTenantGradeLabelsMutation) => res
	},
	UpdateTenantAutoAllocateExcludedGrades: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateTenantAutoAllocateExcludedGradesMutation) => res
	},
	SetTenantPriceEntryUnit: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: SetTenantPriceEntryUnitMutation) => res
	},
	SetTenantAvailabilityQuantityBasis: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: SetTenantAvailabilityQuantityBasisMutation) => res
	},
	SetTenantAvailabilityQuantityEntry: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: SetTenantAvailabilityQuantityEntryMutation) => res
	},
	SetTenantOrderEntryUnit: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: SetTenantOrderEntryUnitMutation) => res
	},
	UpdateProduceAutoLinkMatchingNumbers: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceAutoLinkMatchingNumbersMutation) => res
	},
	UpdateProduceRounding: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceRoundingMutation) => res
	},
	UpdateProducePlanningEntryUnit: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProducePlanningEntryUnitMutation) => res
	},
	UpdateProduceWeekDisplayAnchor: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceWeekDisplayAnchorMutation) => res
	},
	UpdateProduceSpacingDimensionUnit: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceSpacingDimensionUnitMutation) => res
	},
	UpdateProduceSpaceConsumptionBasis: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceSpaceConsumptionBasisMutation) => res
	},
	UpdateProduceDefaultReliefPercent: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceDefaultReliefPercentMutation) => res
	},
	UpdateProduceProductionEntryReview: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProduceProductionEntryReviewMutation) => res
	},
	UpdateProducePlanFromOrderAccess: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateProducePlanFromOrderAccessMutation) => res
	},
	UpdatePricingCalcDecimals: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdatePricingCalcDecimalsMutation) => res
	},
	UpdateBusinessTimeZone: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		transformResponse: (res: UpdateBusinessTimeZoneMutation) => res
	},
	SetTenantLoadPaperworkBinding: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		async onQueryStarted(
			arg: { input: SetTenantLoadPaperworkBindingInput },
			{ dispatch, queryFulfilled }: SetTenantLoadPaperworkBindingLifecycle
		) {
			const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
			const patch = dispatch(
				apiEndpoints.util.updateQueryData("GetTenant", undefined, (draft) =>
					applyLoadPaperworkBindingPatch(arg.input, draft)
				)
			);
			try {
				await queryFulfilled;
			} catch {
				patch.undo();
			}
		},
		transformResponse: (res: SetTenantLoadPaperworkBindingMutation) => res
	},
	SetTenantLoadPaperworkPolicy: {
		invalidatesTags: ["Tenant"] as AddTagTypes,
		async onQueryStarted(
			arg: { input: SetTenantLoadPaperworkPolicyInput },
			{ dispatch, queryFulfilled }: SetTenantLoadPaperworkPolicyLifecycle
		) {
			const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
			const patch = dispatch(
				apiEndpoints.util.updateQueryData("GetTenant", undefined, (draft) =>
					applyLoadPaperworkPolicyPatch(arg.input, draft)
				)
			);
			try {
				await queryFulfilled;
			} catch {
				patch.undo();
			}
		},
		transformResponse: (res: SetTenantLoadPaperworkPolicyMutation) => res
	},
	// REST endpoint injected by onboardingUploadApi.ts — this only attaches the cache tag.
	UploadOnboardingFile: {
		invalidatesTags: ["Tenant", "OnboardingJourney"] as AddTagTypes
	}
};
